import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import type { AssetAssignment, Custodian } from '../api';
import { useEntregar, useLocations } from '../hooks';
import { CustodianAutocomplete } from './CustodianAutocomplete';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';

interface Props {
  open: boolean;
  assetId: string;
  onClose: () => void;
  onSuccess: (assignment: AssetAssignment) => void;
}

export function EntregaModal({ open, assetId, onClose, onSuccess }: Props) {
  const mutation = useEntregar(assetId);
  const locationsQuery = useLocations();
  const [custodian, setCustodian] = useState<Custodian | null>(null);
  const [deliveredAt, setDeliveredAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [locationId, setLocationId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setCustodian(null);
    setDeliveredAt(new Date().toISOString().slice(0, 10));
    setLocationId(''); setNotes(''); setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!custodian) { setError('Selecione o responsável'); return; }
    try {
      const result = await mutation.mutateAsync({
        custodianId: custodian.id,
        deliveredAt: new Date(deliveredAt).toISOString(),
        locationId: locationId || undefined,
        notes: notes || undefined,
      });
      reset();
      onSuccess(result);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao registrar entrega'));
    }
  }

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-gray-900">Registrar entrega do ativo</Dialog.Title>
            <form onSubmit={handleSubmit} className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Responsável *</label>
                <CustodianAutocomplete
                  value={custodian}
                  onChange={setCustodian}
                />
              </div>
              <TextInput
                label="Data da entrega *"
                type="date"
                value={deliveredAt}
                onChange={(e) => setDeliveredAt(e.target.value)}
                required
              />
              <AutocompleteSelect
                label="Localização (opcional)"
                options={[
                  { value: '', label: 'Sem alteração' },
                  ...(locationsQuery.data?.data ?? []).map((l) => ({ value: l.id, label: l.name })),
                ]}
                value={locationId}
                onChange={setLocationId}
              />
              <Textarea
                label="Observações (opcional)"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />

              {error && <Alert variant="error">{error}</Alert>}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
                <Button type="submit" loading={mutation.isPending}>
                  {mutation.isPending ? 'Registrando…' : 'Registrar entrega'}
                </Button>
              </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
